import { useState } from "react";

const MAX_FILES = 8;
const MAX_MB = 8;
const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "application/pdf"
];

function makeId(prefix = "att") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Unable to read file."));
    reader.readAsDataURL(file);
  });
}

function formatSize(bytes) {
  const value = Number(bytes || 0);
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  if (value >= 1024) return `${Math.round(value / 1024)} KB`;
  return `${value} B`;
}

export default function AttachmentManager({ attachments = [], onChange, label = "Attachments" }) {
  const [viewer, setViewer] = useState(null);

  async function addFiles(event) {
    try {
      const files = Array.from(event.target.files || []);
      const prepared = [];

      for (const file of files.slice(0, MAX_FILES)) {
        if (!ALLOWED_TYPES.includes(file.type)) {
          throw new Error("Only JPG, PNG, WEBP, GIF, and PDF attachments are supported.");
        }

        if (file.size > MAX_MB * 1024 * 1024) {
          throw new Error(`Attachment "${file.name}" is too large. Max size is ${MAX_MB} MB.`);
        }

        prepared.push({
          id: makeId(),
          name: file.name,
          type: file.type,
          size: file.size,
          dataUrl: await readFileAsDataUrl(file),
          createdAt: new Date().toISOString()
        });
      }

      onChange([...attachments, ...prepared].slice(0, MAX_FILES));
      event.target.value = "";
    } catch (err) {
      alert(err?.message || "Unable to add attachment.");
      event.target.value = "";
    }
  }

  function remove(id) {
    onChange(attachments.filter((item) => item.id !== id));
  }

  return (
    <div className="attachment-panel">
      <label>
        <span>{label}</span>
        <input type="file" multiple accept="image/*,.pdf" onChange={addFiles} />
      </label>

      <div className="attachment-help">
        JPG, PNG, WEBP, GIF, or PDF. Up to {MAX_FILES} files, {MAX_MB} MB each.
      </div>

      {attachments.length ? (
        <div className="attachment-grid">
          {attachments.map((file) => (
            <div className="attachment-card" key={file.id}>
              <button type="button" className="attachment-preview" onClick={() => setViewer(file)}>
                {String(file.type || "").startsWith("image/") ? (
                  <img src={file.dataUrl} alt={file.name} />
                ) : (
                  <span>PDF</span>
                )}
              </button>

              <div className="attachment-name" title={file.name}>{file.name}</div>
              <div className="attachment-meta">{formatSize(file.size)}</div>

              <div className="attachment-actions">
                <button type="button" onClick={() => setViewer(file)}>View</button>
                <button type="button" className="danger-text" onClick={() => remove(file.id)}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {viewer ? (
        <div className="attachment-viewer" onClick={() => setViewer(null)}>
          <div className="attachment-viewer-inner" onClick={(e) => e.stopPropagation()}>
            <div className="section-head">
              <div>
                <h2>{viewer.name}</h2>
                <p className="muted">{formatSize(viewer.size)}</p>
              </div>
              <button type="button" onClick={() => setViewer(null)}>Close</button>
            </div>

            {String(viewer.type || "").startsWith("image/") ? (
              <img className="attachment-large" src={viewer.dataUrl} alt={viewer.name} />
            ) : (
              <iframe title={viewer.name} src={viewer.dataUrl} className="attachment-pdf" />
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function AttachmentSummary({ attachments = [] }) {
  if (!attachments.length) return null;

  return (
    <div className="attachment-summary">
      <b>{attachments.length}</b> attachment{attachments.length === 1 ? "" : "s"}
    </div>
  );
}
